const {
  checkForRequirements,
  buildAnalyticsBaseContext,
  buildIndicatorQueryContext,
  calculateDeltaPct,
  roundTo2
} = require('../utils')
const { getGrossProfitTotal } = require('./gross-profit')

// A loss/expiry row without a resolved unit_cost yet is excluded from the totals below,
// the same way an uncosted sale is excluded from COGS on the gross profit side. The
// missing-costs banner is what flags the figures as incomplete.
const COSTED_ROW_CONDITION = 'unit_cost IS NOT NULL'

function appendLossesScopeAndProductFilters ({ params, whereClauses, locationScope, productSku, serviceIds }) {
  if (locationScope.scopeType !== 'national') {
    const exactIndex = params.length + 1
    const descendantIndex = params.length + 2
    whereClauses.push(`
        AND (
          dl.fsid = $${exactIndex}
          OR dl.fsid LIKE $${descendantIndex}
        )
    `)
    params.push(locationScope.location, `${locationScope.location}:%`)
  }

  if (productSku) {
    whereClauses.push(`AND li.product_sku_id = $${params.length + 1}`)
    params.push(productSku)
  }

  if (serviceIds && serviceIds.length > 0) {
    whereClauses.push(`AND li.service_id = ANY($${params.length + 1}::uuid[])`)
    params.push(serviceIds)
  }
}

async function getLossesTotals ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const { currentMonthDays, previous3MonthsPeriodDays, params, whereClauses } = buildIndicatorQueryContext({
    organisationId,
    fromDateUtc,
    toDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds,
    appendFilters: appendLossesScopeAndProductFilters
  })

  const query = `
      WITH filtered AS (
        SELECT
          li.reported_at,
          li.units_lost::numeric AS units_lost,
          li.units_expired::numeric AS units_expired,
          li.unit_cost
        FROM analytics.loss_indicators li
        INNER JOIN avocado.data_location dl
          ON dl.uuid = li.location_id
        WHERE li.organisation_id = $1
          AND li.reported_at >= $2::timestamptz
          AND li.reported_at < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        COUNT(*) FILTER (WHERE reported_at >= $6::timestamptz AND reported_at < $5::timestamptz)::int AS total_rows,
        COUNT(*) FILTER (WHERE ${COSTED_ROW_CONDITION} AND reported_at >= $6::timestamptz AND reported_at < $5::timestamptz)::int AS costed_rows,
        COUNT(*) FILTER (WHERE unit_cost IS NULL AND reported_at >= $6::timestamptz AND reported_at < $5::timestamptz)::int AS missing_cost_rows,
        COALESCE(
          SUM(units_lost * unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND reported_at >= $6::timestamptz
              AND reported_at < $5::timestamptz
          ),
          0
        )::numeric AS other_losses_total,
        COALESCE(
          SUM(units_expired * unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND reported_at >= $6::timestamptz
              AND reported_at < $5::timestamptz
          ),
          0
        )::numeric AS cost_of_expiry_total,
        COALESCE(
          SUM((COALESCE(units_lost, 0) + COALESCE(units_expired, 0)) * unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND reported_at >= $4::timestamptz
              AND reported_at < $5::timestamptz
          ),
          0
        )::numeric AS current_month_losses_total,
        COALESCE(
          SUM((COALESCE(units_lost, 0) + COALESCE(units_expired, 0)) * unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND reported_at >= $2::timestamptz
              AND reported_at < $4::timestamptz
          ),
          0
        )::numeric AS previous_3_months_losses_total
      FROM filtered
    `

  const { rows } = await pgConnection.query(query, params)
  const row = rows[0] || {}
  const otherLossesTotal = Number(row.other_losses_total || 0)
  const costOfExpiryTotal = Number(row.cost_of_expiry_total || 0)
  const lossesTotal = roundTo2(otherLossesTotal + costOfExpiryTotal)
  const currentAverageDailyLosses = Number(row.current_month_losses_total || 0) / currentMonthDays
  const previous3MonthsAverageDailyLosses = Number(row.previous_3_months_losses_total || 0) / previous3MonthsPeriodDays
  const lossesDeltaPercent = calculateDeltaPct(currentAverageDailyLosses, previous3MonthsAverageDailyLosses)

  return {
    fromDate,
    toDate,
    organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    totalRows: Number(row.total_rows || 0),
    costedRows: Number(row.costed_rows || 0),
    missingCostRows: Number(row.missing_cost_rows || 0),
    otherLossesTotal: roundTo2(otherLossesTotal),
    costOfExpiryTotal: roundTo2(costOfExpiryTotal),
    lossesTotal,
    previous3MonthsAverageDailyLosses: roundTo2(previous3MonthsAverageDailyLosses),
    currentAverageDailyLosses: roundTo2(currentAverageDailyLosses),
    lossesDeltaPercent
  }
}

async function getLossesSummary ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  const [lossesTotals, grossProfitTotal] = await Promise.all([
    getLossesTotals({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }),
    getGrossProfitTotal({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds })
  ])

  const lossesPercentOfGrossProfit = grossProfitTotal === 0
    ? null
    : roundTo2((lossesTotals.lossesTotal / grossProfitTotal) * 100)
  const grossProfitAfterLosses = roundTo2(grossProfitTotal - lossesTotals.lossesTotal)

  return {
    ...lossesTotals,
    grossProfitTotal: roundTo2(grossProfitTotal),
    lossesPercentOfGrossProfit,
    grossProfitAfterLosses
  }
}

module.exports = {
  getLossesTotals,
  getLossesSummary
}
